import React from "react";
import {
  IconButton,
  Typography,
  Tooltip,
  Stack,
  Box,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { useFormikContext } from "formik";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import EditIcon from "@mui/icons-material/Edit";
import ChecklistIcon from "@mui/icons-material/Checklist";

const DragHandle = ({ attributes, listeners }) => (
  <DragHandleIcon
    sx={{ cursor: "grab", verticalAlign: "middle" }}
    {...attributes}
    {...listeners}
  />
);

// Sortable product row: drag handle, image, name, price (matches project list style)
const SortableProductRow = ({
  id,
  product,
  defaultLanguage,
  allProducts,
  currencyCode,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const item = allProducts[product];
  const displayName =
    item?.productName?.[defaultLanguage] ||
    item?.productName ||
    item?.name ||
    "";
  const price = item?.price ?? item?.salePrice ?? "";
  const priceFormatted =
    price !== "" && Number.isFinite(Number(price))
      ? `${Number(price).toFixed(2)} ${currencyCode || ""}`.trim()
      : "—";
  const imageSrc =
    item?.customProductImageUrl || item?.image || "/images/noImagePlaceholder.png";

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 0.75,
        px: 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Div
        {...attributes}
        {...listeners}
        sx={{ cursor: "grab", touchAction: "none", color: "text.secondary", flexShrink: 0 }}
      >
        <DragHandleIcon fontSize="small" />
      </Div>
      <Box
        component="img"
        src={imageSrc}
        alt=""
        width={40}
        height={40}
        sx={{
          objectFit: "cover",
          borderRadius: 1,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {displayName || "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {priceFormatted}
      </Typography>
    </Stack>
  );
};

function resolveBindingDisplayName(binding, bindingsList, defaultLanguage) {
  const bindingName = binding?.name;
  if (typeof bindingName === "string" && bindingName.trim()) {
    return bindingName.trim();
  }
  if (bindingName && typeof bindingName === "object") {
    const n =
      bindingName[defaultLanguage] ||
      bindingName.sk ||
      bindingName.en ||
      bindingName.SK ||
      bindingName.EN ||
      Object.values(bindingName).find(
        (v) => typeof v === "string" && v.trim()
      );
    if (n != null && String(n).trim() !== "") return String(n).trim();
  }
  const bindingId = binding?.bindingId;
  if (bindingId && Array.isArray(bindingsList) && bindingsList.length > 0) {
    const found = bindingsList.find(
      (b) => String(b._id) === String(bindingId)
    );
    if (found) {
      const src =
        (found.bindingName && typeof found.bindingName === "object" && found.bindingName) ||
        (found.name && typeof found.name === "object" && found.name) ||
        {};
      const n =
        src[defaultLanguage] ||
        src.sk ||
        src.en ||
        src.SK ||
        src.EN ||
        Object.values(src).find((v) => typeof v === "string" && v.trim());
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (typeof found.name === "string") return found.name;
    }
  }
  return "";
}

const BindingItemSortable = ({
  id,
  binding,
  bindingsList = [],
  defaultLanguage,
  code,
  allProducts,
  currencyCode,
  onAddGroup,
  onAddBinding,
  onDelete,
  onChipSortEnd,
}) => {
  let name = resolveBindingDisplayName(binding, bindingsList, defaultLanguage);
  if (binding?.required && name) {
    name = `${name} *`;
  }
  const { t, i18n } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const { setFieldValue, values } = useFormikContext();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get active products for this binding
  const activeProducts = Object.keys(binding?.products || {}).filter(
    (product) => binding?.products[product] && binding?.products[product].status
  );

  const handleChipDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = activeProducts.findIndex((item) => item === active.id);
      const newIndex = activeProducts.findIndex((item) => item === over.id);

      if (onChipSortEnd) {
        onChipSortEnd(code, oldIndex, newIndex);
      }
    }
  };

  return (
    <Box
      sx={{
        zIndex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: "400px",
        overflow: "hidden",
      }}
      ref={setNodeRef}
      style={style}
    >
      {/* Fixed Header */}
      <Box
        flexDirection="row"
        justifyContent="space-between"
        display="flex"
        width="100%"
        sx={{
          backgroundColor: "background.paper",
          zIndex: 10,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Div sx={{ display: "flex", gap: 1, flex: 1, alignItems: "center" }}>
          <DragHandle attributes={attributes} listeners={listeners} />
          <Typography sx={{ flex: 1, flexShrink: 0 }}>{name}</Typography>
        </Div>
        <Div sx={{ display: "flex", gap: 1 }}>
          <IconButton
            sx={{ p: 0.5 }}
            onClick={(e) => {
              showDialog({
                variant: "confirm",
                title: t("widgets.confirmDialog.areYouSure"),
                onYes: () => onDelete(code, values, setFieldValue),
                onNo: () => hideDialog(),
              });
            }}
          >
            <Tooltip title={t("buttons.delete")} placement="top-start">
              <DeleteForeverIcon />
            </Tooltip>
          </IconButton>
          <IconButton
            sx={{ p: 0.5 }}
            onClick={(e) => {
              onAddGroup(code, values, setFieldValue);
            }}
          >
            <Tooltip title={t("buttons.edit")} placement="top-start">
              <EditIcon />
            </Tooltip>
          </IconButton>
          <IconButton sx={{ p: 0.5 }} onClick={() => onAddBinding(code)}>
            <Tooltip
              title={t("pages.dsokProducts.listOfItems")}
              placement="top-start"
            >
              <ChecklistIcon />
            </Tooltip>
          </IconButton>
        </Div>
      </Box>

      {/* Product list with sortable rows (always visible) */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1,
          py: 1,
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleChipDragEnd}
        >
          <SortableContext
            items={activeProducts}
            strategy={verticalListSortingStrategy}
          >
            {activeProducts.map((product) => (
              <SortableProductRow
                key={product}
                id={product}
                product={product}
                defaultLanguage={defaultLanguage}
                allProducts={allProducts}
                currencyCode={currencyCode}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};
/* Todo item prop define */
export default BindingItemSortable;
